import { useState } from "react";
import { useToast } from "@/components/shared/Toast";
import { exportService } from "@/lib/services";
import type { ChatSession, ExportCapabilities } from "@/types";

/**
 * Encapsulates all export-related state and logic extracted from Chat.tsx.
 *
 * Manages the export modal visibility, format/template selection,
 * capabilities loading, and the actual download flow.
 */
export function useChatExport(currentSession: ChatSession | null) {
  const { showToast } = useToast();

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState("docx");
  const [exportTemplate, setExportTemplate] = useState("clean");
  const [exportContent, setExportContent] = useState("full");
  const [exportIncludeSystem, setExportIncludeSystem] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportCapabilities, setExportCapabilities] = useState<ExportCapabilities | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const openExportModal = async () => {
    setIsExportModalOpen(true);
    setExportError(null);
    // Load capabilities if not already loaded
    if (!exportCapabilities) {
      try {
        const caps = await exportService.getCapabilities();
        setExportCapabilities(caps);
      } catch (err) {
        console.error("Failed to load export capabilities:", err);
      }
    }
  };

  const closeExportModal = () => {
    setIsExportModalOpen(false);
  };

  const handleExport = async () => {
    if (!currentSession) return;

    try {
      setIsExporting(true);
      setExportError(null);

      const blob = await exportService.exportSession(currentSession.uuid, {
        format: exportFormat,
        template: exportTemplate,
        content: exportContent,
        includeSystemMessages: exportIncludeSystem,
      });

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      // Determine file extension
      const ext = exportFormat === "md" ? "md" : exportFormat;
      const safeName = (currentSession.title || "chat-export")
        .replace(/[^a-zA-Z0-9-_ ]/g, "")
        .replace(/\s+/g, "_")
        .slice(0, 50);
      a.download = `${safeName}.${ext}`;

      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      setIsExportModalOpen(false);
      showToast(`Chat exported as ${exportFormat.toUpperCase()} successfully`, "success");
    } catch (err) {
      console.error("Export failed:", err);
      const errorMsg = err instanceof Error ? err.message : "Export failed. Please try again.";
      setExportError(errorMsg);
      showToast("Export failed. Please try again.", "error");
    } finally {
      setIsExporting(false);
    }
  };

  return {
    // State
    isExportModalOpen,
    exportFormat,
    exportTemplate,
    exportContent,
    exportIncludeSystem,
    isExporting,
    exportCapabilities,
    exportError,
    // Setters (for UI bindings)
    setExportFormat,
    setExportTemplate,
    setExportContent,
    setExportIncludeSystem,
    // Actions
    openExportModal,
    closeExportModal,
    handleExport,
  };
}
